import { InformationCircleIcon } from "@heroicons/react/24/outline";
import type { MouseEvent } from "react";
import { ServiceTitanIconLink } from "@/core/components/service-titan-icon-link";
import { Button } from "@/core/components/ui/button";
import { Stack } from "@/core/components/ui/stack";
import {
  InvoiceCardTitleLink,
  ManualJobMatchButton,
  StatusBadge,
} from "@/accounts-payable/common";
import type { ApReviewQueueItem } from "@/accounts-payable/types";
import {
  AP_TAB,
  canManuallyMatchJob,
  formatApDate,
  invoiceStatusMeta,
  isBulkApprovable,
  isPendingReviewAction,
  stJobUrl,
} from "@/accounts-payable/utils";
import { formatCentsOrDashIfZero } from "@/core/utils/money";
import { cn } from "@/core/utils/utils";
import { ActionRow } from "./action-row";

/**
 * Shown on an invoice with no job. Matching one supersedes every pending
 * proposal on the invoice (`Ap::ManualJobMatchService#supersede_outstanding_proposals`)
 * and starts a fresh run, so anything decided here first is provisional.
 *
 * A band butted against the header rather than `Alert`: this qualifies the
 * invoice above it, and a bounded rounded callout floating in the card read as
 * a louder, separate object. `Alert` is also `role="alert"`, which is for
 * things that just happened — this is a standing condition.
 */
const SupersedeNotice = () => (
  <Stack
    direction="row"
    gap="sm"
    align="center"
    fullWidth
    role="note"
    className="border-b border-blue-20 bg-blue-5 px-6 py-2.5"
  >
    <InformationCircleIcon className="h-4 w-4 flex-none text-blue-70" />
    <span className="text-md text-primary-text">
      These proposals were made without a job. Matching one re-runs the agent
      and replaces them.
    </span>
  </Stack>
);

export const InvoiceGroupCard = ({
  item,
  businessId,
  onResolve,
  enableBulkApprove,
  onApproveAll,
  bulkApproving,
  onOpenInvoice,
}: {
  item: ApReviewQueueItem;
  businessId: string | null;
  onResolve: (id: string, decision: "approved" | "rejected") => void;
  enableBulkApprove?: boolean;
  onApproveAll?: () => void;
  bulkApproving?: boolean;
  onOpenInvoice?: (id: string, event: MouseEvent<HTMLAnchorElement>) => void;
}) => {
  // Only surface the actions that still need review.
  const reviewActions = item.last_completed_agent_run.actions.filter(
    isPendingReviewAction,
  );
  const needsJob = canManuallyMatchJob(item) && !item.st_job_external_id;
  // "Approve all" is meaningless on a group whose only rows are acknowledgements.
  const approvableCount = reviewActions.filter(isBulkApprovable).length;
  return (
    <Stack
      data-review-invoice-id={item.id}
      gap="none"
      fullWidth
      className="overflow-hidden rounded-lg border border-stroke-tertiary bg-card pb-2"
    >
      {/* Invoice header. The status badge closes the title line rather than
          opening it, so vendor names align down the queue. */}
      <Stack
        direction="row"
        justify="between"
        align="center"
        gap="md"
        fullWidth
        className="flex-wrap bg-bg-secondary/50 px-6 py-4"
      >
        <Stack gap="none" className="min-w-0 flex-1">
          {/* Line one is identity: who, and what state the invoice is in. */}
          <Stack direction="row" gap="sm" align="center" className="flex-wrap">
            <span className="truncate text-lg font-semibold">
              {item.vendor_name}
            </span>
            <StatusBadge meta={invoiceStatusMeta(item)} />
          </Stack>
          {/* The amount is pinned to the end of this second line, a fixed gap
              past the metadata, so it tracks the address line specifically
              rather than the wider of the two header lines. */}
          <Stack direction="row" gap="md" align="center" className="mt-1.5">
            {/* Everything that identifies the paperwork sits on one line at one
                size, so the two links don't read as different kinds of thing.
                The queue sorts by created_at (arrival), not invoice_date. */}
            <Stack
              direction="row"
              gap="xs"
              align="center"
              className="min-w-0 max-w-[38rem] flex-wrap text-md text-tertiary-text"
            >
              <InvoiceCardTitleLink
                invoiceId={item.id}
                vendorName={item.vendor_name}
                invoiceNumber={item.invoice_number}
                fromTab={AP_TAB.review}
                onClick={(event) => onOpenInvoice?.(item.id, event)}
              />
              {item.st_job_external_id && (
                <>
                  <span aria-hidden>·</span>
                  <ServiceTitanIconLink
                    url={stJobUrl(item.st_job_external_id)}
                    label={`Open job #${item.st_job_external_id} in ServiceTitan`}
                    className="underline underline-offset-2"
                  >
                    Job #{item.st_job_external_id}
                  </ServiceTitanIconLink>
                </>
              )}
              <span aria-hidden>·</span>
              <span>{`Received ${formatApDate(item.created_at)}`}</span>
              {item.service_address && (
                <>
                  <span aria-hidden>·</span>
                  <span className="truncate">{item.service_address}</span>
                </>
              )}
            </Stack>
            {/* Rendered only when there is a total, so an invoice without one
                shows no orphaned divider. A "-" implied a value of zero. */}
            {item.total_amount_cents ? (
              <Stack
                direction="row"
                gap="md"
                align="center"
                className="flex-none"
              >
                <span className="h-5 w-px bg-stroke-tertiary" aria-hidden />
                <span
                  className={cn(
                    "text-md font-semibold tabular-nums text-primary-text",
                    { "text-negative-text": item.total_amount_cents < 0 },
                  )}
                >
                  {formatCentsOrDashIfZero(item.total_amount_cents)}
                </span>
              </Stack>
            ) : null}
          </Stack>
        </Stack>
        {/* Mirrors a row's [primary pill][gap][✕] trailing structure — the ✕
            slot here is an empty spacer — so "Approve all" / "Match to a job"
            land on the exact left and right edges of each row's Approve.
            -mr-2 matches the rows' overhang. The pill cell keeps its width with
            or without a button, so the amount sits at one x down the queue. */}
        <Stack
          direction="row"
          gap="sm"
          align="center"
          justify="end"
          className="-mr-2 flex-none"
        >
          <Stack
            direction="row"
            align="center"
            justify="end"
            className="min-w-[7.5rem] flex-none"
          >
            {needsJob && (
              <ManualJobMatchButton
                businessId={businessId}
                documentId={item.id}
                agentRunId={item.agent_run_id}
                size="xsPill"
                className="min-w-[7.5rem]"
              />
            )}
            {!needsJob && enableBulkApprove && approvableCount > 1 && (
              <Button
                size="xsPill"
                variant="tertiary"
                className="min-w-[7.5rem]"
                loading={bulkApproving}
                onClick={onApproveAll}
              >
                Approve all
              </Button>
            )}
          </Stack>
          <span className="h-8 w-8 flex-none" aria-hidden />
        </Stack>
      </Stack>

      {needsJob && <SupersedeNotice />}

      {/* The actions awaiting review — inset rules, so the section's own
          full-bleed rule stays the stronger division. */}
      {reviewActions.map((a, i) => (
        <ActionRow
          key={a.id}
          action={a}
          runActions={item.last_completed_agent_run.actions}
          businessId={businessId}
          document={{
            id: item.id,
            type: item.last_completed_agent_run.document_type,
            number: item.invoice_number,
            agentRunId: item.agent_run_id,
            hasJob: !!item.st_job_external_id,
          }}
          showDivider={i > 0}
          onResolve={(decision) => onResolve(a.id, decision)}
        />
      ))}
    </Stack>
  );
};
